import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_VideoWatermarkRemover = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/video-watermark-remover',
			name: 'Video Watermark Remover — Remove Watermark from Video Online | Lift',
			description:
				"Remove a logo from a video, erase a channel stamp, or clear an overlay — Lift's AI scans every frame, finds the mark on its own, and deletes it in seconds. Try Lift now!",
			url: 'https://liftapp.ai/video-watermark-remover',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/video-watermark-remover#product' },
				{ '@id': 'https://liftapp.ai/video-watermark-remover#howto' },
				{ '@id': 'https://liftapp.ai/video-watermark-remover#benefits' },
				{ '@id': 'https://liftapp.ai/video-watermark-remover#faq' },
				{ '@id': 'https://liftapp.ai/video-watermark-remover#why-lift' },
				{ '@id': 'https://liftapp.ai/video-watermark-remover#explore-tools' },
				{ '@id': 'https://liftapp.ai/video-watermark-remover#all-in-one' },
			],
		},
		// product
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/video-watermark-remover#product',
			name: 'Video Watermark Remover',
			description:
				"Erase watermarks, logos, and stamps from video with Lift's AI. Upload a clip, the AI tracks the mark through every frame and rebuilds the footage behind it — no masking, no frame-by-frame work.",
			url: 'https://liftapp.ai/video-watermark-remover',
			fileFormat: [
				'video/mp4',
				'video/quicktime',
				'video/x-msvideo',
				'video/webm',
				'video/x-matroska',
				'video/mpeg',
			],
			memoryRequirements: 'Max file size: 2GB',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: '4.8',
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		// howto
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/video-watermark-remover#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Upload Your Video',
					text: 'Drop in a clip from your device — all common video formats are supported.',
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'Let AI Detect And Remove',
					text: 'Removal starts automatically: Lift tracks the watermark, logo, or stamp through every frame and rebuilds the footage behind it.',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Review The Result',
					text: 'Play the cleaned clip in the built-in player, step back to the original with Undo to compare, and tap Retry for a fresh pass.',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Download Your Result',
					text: 'Save the clean video to your device in its original format, ready to post or share.',
				},
			],
		},
		// benefits
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/video-watermark-remover#benefits',
			name: 'Who can benefit from Video Watermark Remover?',
			description:
				"Lift's Video Watermark Remover helps content creators, online sellers, videographers, editors, and everyday users clear logos, stamps, and overlays from any clip in one pass.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Content creators — Old channel stamps and app logos on your own footage make a feed look inconsistent. Clear them before posting so every reel, short, and vlog looks polished.',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Online sellers and marketers — Product clips from supplier catalogs often arrive with a logo in the corner. Lift works as a video logo remover in one pass, leaving clean footage ready for listings, ads, and campaigns.',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Videographers — Preview cuts go out with a studio mark, but clients need the final version clean. Skip frame-by-frame masking and let AI restore the footage behind the mark in seconds.',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Video editors — Review cuts and drafts usually carry an approval stamp. Once the version is signed off, Lift clears the mark so the clip drops straight into the timeline.',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'Everyday users — A date stamp or a camera app logo can spoil a great memory. Wipe it away from any video and keep the moment exactly as you remember it.',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/benefits.3da6de53.webp',
				name: 'Video Watermark Remover by Lift',
				description:
					"Clip with a logo next to the same clip after Lift's watermark remover from video cleaned it.",
			},
		},
		// faq
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/video-watermark-remover#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is the Lift Video Watermark Remover?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Video Watermark Remover is Lift's AI tool for deleting watermarks, logos, and stamps from video clips. You upload a file, the AI detects every mark on its own, tracks it through the whole clip, removes it, and reconstructs the footage so the result looks as if the watermark was never there.",
					},
				},
				{
					'@type': 'Question',
					name: 'How do I remove a watermark from a video?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Upload your clip to Lift and the removal begins automatically — there is nothing to select or outline. When the AI finishes, play the result, tap Retry if you want another pass, and download the clean version.',
					},
				},
				{
					'@type': 'Question',
					name: 'Will the quality of my video stay the same?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes. Lift only rebuilds the area under the watermark and leaves the rest of the footage untouched, so colors, sharpness, and motion stay exactly as they were. The AI fills the cleared spot with matching texture and lighting in every frame, so there are no flickers or patches.',
					},
				},
				{
					'@type': 'Question',
					name: 'Can Lift remove more than one watermark from the same video?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes. The AI scans the whole clip, frame by frame, and picks up every watermark, logo, or stamp it finds — not just the largest one. All of them are removed in a single pass, and the footage behind each is rebuilt separately so the result stays seamless.',
					},
				},
				{
					'@type': 'Question',
					name: 'Is it safe to upload my videos to Lift?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes — Lift processes your videos securely and uses them only to generate your results.',
					},
				},
			],
		},
		// why-lift
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/video-watermark-remover#why-lift',
			name: 'Why Lift Video Watermark Remover',
			description:
				'Discover the advantages of Lift Video Watermark Remover: high-quality results, intelligent rendering, cross-platform support, and budget-friendly pricing.',
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'High-quality results',
					description:
						'AI-powered technology delivers professional-grade visuals every time',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Intelligent rendering',
					description:
						'AI tailors the effect to the scene and subject for optimal results',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly designers with an affordable and intuitive tool',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/whyLift.7c19de84.webp',
				name: 'Why use Lift Video Watermark Remover',
				description:
					"Clean video result after using Lift's AI video watermark remover.",
			},
		},
		// explore-tools
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/video-watermark-remover#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},
		// all-in-one
		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/video-watermark-remover#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
				},
			],
		},
		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Video Watermark Remover',
					item: 'https://liftapp.ai/video-watermark-remover',
				},
			],
		},
	],
};
